import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IoMdClose } from 'react-icons/io';
import { FaAngleLeft } from 'react-icons/fa6';

import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { BsChevronRight } from 'react-icons/bs';

import useCartActions from '../../hooks/useCartActions';
import Toast from '../Toast';
import DOMPurify from 'dompurify';
import { OPEN_LOGIN_EVENT } from '../../constants/auth';
import { useUser } from '../../contexts/user';
import { isMerchantServiceable } from '../../utils/serviceability';
import { SUPPORT_EMAIL } from '../../config/appConfig';

import api from '../../api/api';
import log from '../../utils/logger';

const VariantItem = ({ variant, index, selectedVariantIndex, setSelectedVariantIndex, i18n }) => {
  const [clicked, setClicked] = useState(false);

  const handleClick = () => {
    setSelectedVariantIndex(index);
    setClicked(false);
    requestAnimationFrame(() => setClicked(true));
  };

  return (
    <div
      key={variant._id || index}
      className="relative flex items-center gap-3 p-2 my-1 rounded-xl cursor-pointer bg-white"
      onClick={handleClick}
    >
      {clicked && (
        <span
          className="absolute inset-0 bg-[#888888] opacity-30 rounded-xl animate-pulse-select"
          onAnimationEnd={() => setClicked(false)}
        ></span>
      )}

      <div className="w-5 h-5 rounded-full border-2 my-2 flex items-center justify-center border-[#dedede]">
        {selectedVariantIndex === index && (
          <div className="w-[10px] h-[10px] bg-black rounded-full" />
        )}
      </div>
      <span className="text-sm">
        {i18n.language.startsWith('fr') ? variant.frVariantName : variant.enVariantName}
      </span>
    </div>
  );
};

const SingleProductDetails = ({ product, onClose, merchant, cartQuantities }) => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const { addToCart, toastMessage, toastType, showToast } = useCartActions();
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const mainSwiperRef = useRef(null);
  const [visible, setVisible] = useState(true);
  const [localProduct, setLocalProduct] = useState(product);

  const [localQuantity, setLocalQuantity] = useState(() => {
    if (localProduct?.variants?.length > 0) {
      const variantId = localProduct.variants[0].id;
      return (
        cartQuantities?.[variantId] ||
        localProduct.minQuantity ||
        1
      );
    }

    return (
      cartQuantities?.default ||
      localProduct.minQuantity ||
      1
    );
  });

  useEffect(() => {
    if (!localProduct?.variants?.length) {
      setLocalQuantity(
        cartQuantities?.default ||
        localProduct.minQuantity ||
        1,
      );
      return;
    }

    const variantId = localProduct.variants[selectedVariantIndex]?.id;

    setLocalQuantity(
      cartQuantities?.[variantId] ||
      localProduct.minQuantity ||
      1,
    );
  }, [selectedVariantIndex, localProduct, cartQuantities]);

  useEffect(() => {
    const handleLogin = async () => {
      try {
        const response = await api.get(`/buyer/store/products/${localProduct._id}`);

        setLocalProduct(response.data.response.data);
      } catch (err) {
        log.error(err);
      }
    };

    window.addEventListener('user-logged-in', handleLogin);

    return () => {
      window.removeEventListener('user-logged-in', handleLogin);
    };
  }, [user]);

  // When the parent passes a `merchant` and that merchant doesn't deliver
  // to the user's saved address, swap the price / Add-to-cart bar for an
  // out-of-zone notice + "Contacter Neobox" CTA so the buyer can request
  // delivery instead of hitting a dead-end.
  const isOutOfZone =
    merchant && !isMerchantServiceable(merchant, user);

  const buildOutOfZoneMailto = () => {
    const merchantName = merchant?.storeName || '';
    const subject = encodeURIComponent(
      t('outOfZoneEmailSubject', { merchant: merchantName }),
    );
    const body = encodeURIComponent(
      t('outOfZoneEmailBody', { merchant: merchantName }),
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleClose = () => {
    setVisible(false);
  };

  const handleAnimationEnd = () => {
    if (!visible) {
      onClose();
    }
  };

  const MAX_CHARS = 200;

  const rawText =
    (i18n.language.startsWith('fr')
      ? localProduct.frDescription
      : localProduct.enDescription) || '';

  const sanitizedHtml = useMemo(() => {
    return DOMPurify.sanitize(
      rawText.replace(/\\n/g, '<br />'),
    );
  }, [rawText]);

  const exceedsCharLimit = rawText.length > MAX_CHARS;

  const handleMouseMove = (e, imgRef) => {
    const rect = imgRef.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const moveX = ((offsetX - centerX) / centerX) * 150;
    const moveY = ((offsetY - centerY) / centerY) * 150;
    imgRef.style.transform = `scale(1.25) translate(${moveX}px, ${moveY}px)`;
  };

  const handleMouseLeave = (imgRef) => {
    imgRef.style.transform = 'scale(1)';
  };

  const getCurrentPrice = () => {
    if (localProduct?.variants?.length > 0) {
      return localProduct.variants[selectedVariantIndex]?.price ?? null;
    }
    return localProduct?.price ?? null;
  };

  const handleQuantityChange = (change) => {
    const min = localProduct.minQuantity || 1;
    const max = localProduct.maxQuantity || Infinity;
    const newQuantity = localQuantity + change;

    if (newQuantity < min) {
      showToast(t('minQuantityMessage', { qty: min }), 'warning');
      return;
    }

    if (newQuantity > max) {
      showToast(t('maxQuantityMessage', { qty: max }), 'warning');
      return;
    }

    setLocalQuantity(newQuantity);
  };

  const handleAddToCartClick = async () => {
    const variantId = localProduct?.variants?.[selectedVariantIndex]?.id?.toString() ?? undefined;
    await addToCart(localProduct, {
      quantity: localQuantity,
      variantId,
      onSuccess: handleClose,
    });
  };

  const handleQuantityInput = (e) => {
    const value = e.target.value;

    // Allow empty while editing
    if (value === '') {
      setLocalQuantity('');
      return;
    }

    // Only allow digits
    if (!/^\d+$/.test(value)) {
      return;
    }

    setLocalQuantity(Number(value));
  };

  const handleQuantityBlur = () => {
    const min = localProduct.minQuantity || 1;
    const max = localProduct.maxQuantity || Infinity;

    let quantity = Number(localQuantity);

    if (!quantity || Number.isNaN(quantity)) {
      quantity = min;
    }

    if (quantity < min) {
      showToast(t('minQuantityMessage', { qty: min }), 'warning');
      quantity = min;
    }

    if (quantity > max) {
      showToast(t('maxQuantityMessage', { qty: max }), 'warning');
      quantity = max;
    }

    setLocalQuantity(quantity);
  };

  return (
    <div
      className={`fixed inset-0 h-[100dvh] z-50 flex justify-center items-center transition-opacity duration-300
        ${visible ? 'opacity-100' : 'opacity-0'}
      `}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
      onTransitionEnd={handleAnimationEnd}
    >
      <div
        className={`bg-white rounded-t-2xl lg:rounded max-w-[1200px]
          max-h-[100dvh]
          mx-auto w-full lg:w-[95%] lg:max-h-[600px]
          overflow-y-auto lg:overflow-hidden
          overscroll-contain
          grid grid-cols-1 lg:grid-cols-2 lg:h-full transform transition-transform duration-300
          ${visible ? 'animate-slide-up' : 'translate-y-full'}
        `}
      >
        {/* Header Only show at small screens  */}
        <div className='bg-white sticky top-0 w-full px-4 py-3 lg:hidden flex items-center justify-between gap-2 z-50'>
          <button
            className='w-9 h-9 flex items-center justify-center rounded-lg bg-[#f3f3f4]'
            // onClick={onClose}
            onClick={handleClose}
          >
            <FaAngleLeft />
          </button>
          <h2 className='font-semibold text-lg text-[#000000DE] truncate'>{localProduct?.enName || t('Unnamed Product')}</h2>
          <div></div>
        </div>

        {/* Images Section */}
        <div className="flex flex-col bg-white w-full h-full lg:h-[600px] relative justify-center">
          {/* Main Swiper */}
          <div className="relative flex-grow w-full flex flex-col items-center justify-center">
            <Swiper
              spaceBetween={10}
              loop={true}
              slidesPerView={1}
              centeredSlides={true}
              modules={[Navigation]}
              navigation={{
                nextEl: '.custom-swiper-button-next2',
                prevEl: '.custom-swiper-button-prev2',
              }}
              className="w-full h-full"
              onSwiper={(swiper) => (mainSwiperRef.current = swiper)}
            >
              {localProduct?.images?.length > 0 ? (
                localProduct.images.map((src, index) => (
                  <SwiperSlide
                    key={index}
                    className="flex items-center justify-center"
                  >
                    <img
                      src={src}
                      alt={`Product Image ${index + 1}`}
                      className="max-h-[400px] lg:max-h-[500px] w-full object-contain transition-transform duration-200 ease-out cursor-zoom-in"
                      ref={(ref) => (localProduct[`imgRef${index}`] = ref)}
                      onMouseMove={(e) => handleMouseMove(e, localProduct[`imgRef${index}`])}
                      onMouseLeave={() => handleMouseLeave(localProduct[`imgRef${index}`])}
                    />
                  </SwiperSlide>
                ))
              ) : (
                <SwiperSlide className="flex items-center justify-center rounded bg-gray-100">
                  <span className="text-gray-500 text-lg font-medium">{t('noImages')}</span>
                </SwiperSlide>
              )}
            </Swiper>

            {/* Next & Previous Buttons */}
            <button className="custom-swiper-button-prev2 absolute top-1/2 left-0 transform -translate-y-1/2 z-10 hidden md:block">
              <BsChevronRight className="rotate-180 text-[#007AFF] text-4xl" />
            </button>

            <button className="custom-swiper-button-next2 absolute top-1/2 right-0 transform -translate-y-1/2 z-10 hidden md:block">
              <BsChevronRight className='text-[#007AFF] text-4xl' />
            </button>

            {/* Thumbnails */}
            <div className="flex gap-2 mt-2 overflow-x-auto pb-2 justify-center w-full flex-shrink-0">
              {localProduct?.images?.map((src, idx) => (
                <div
                  key={idx}
                  className={`w-16 h-16 flex-shrink-0 rounded border-2 cursor-pointer ${
                    mainSwiperRef.current?.realIndex === idx ? 'border-blue-500' : 'border-gray-300'
                  }`}
                  onClick={() => mainSwiperRef.current?.slideToLoop(idx)}
                >
                  <img
                    src={src}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover rounded"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className='relative flex flex-col h-full min-h-0'>
          {/* Close button */}
          <div className='absolute right-4 top-4 mr-4 hidden lg:flex justify-end'>
            <button
              // onClick={onClose}
              onClick={handleClose}
              className=" w-10 h-10 rounded-lg bg-gray-300 flex items-center justify-center z-30"
            >
              <IoMdClose className="text-xl" />
            </button>
          </div>

          <div className={`flex-1 overflow-y-auto boxscrollbar pt-10 px-4 min-h-0 ${localProduct?.variants?.length > 4 ? 'lg:pb-20' : ''}`}>
            <h4 className="font-medium text-sm">
              {(() => {
                const key = i18n.language.startsWith('fr') ? 'frNames' : 'enNames';
                const names = localProduct?.categories?.[key];
                return Array.isArray(names) && names.length > 0 ? names.join(', ') : '';
              })()}
            </h4>
            <h2 className="font-semibold text-xl leading-7">
              {i18n.language.startsWith('fr') ? localProduct?.frName : localProduct?.enName}
            </h2>
            {localProduct?.compareAtPrice && (
              <small className="text-[#A3A3A3] text-sm leading-4 line-through mt-1">
                {localProduct.compareAtPrice.toFixed(2)}$
              </small>
            )}
            <h4 className="text-lg font-semibold leading-7 mb-2">
              {getCurrentPrice() !== null ? (
                <span className="block">{getCurrentPrice().toFixed(2)}$</span>
              ):(
                <span className="inline-flex items-center text-xs text-[#999]">
                  {t('noPriceMessage')}
                </span>
              )}
            </h4>

            <div className="mb-10">
              <p
                className="text-sm leading-5 text-black"
                dangerouslySetInnerHTML={{
                  __html: expanded || !exceedsCharLimit
                    ? sanitizedHtml
                    : DOMPurify.sanitize(
                      rawText.slice(0, MAX_CHARS).replace(/\\n/g, '<br />') + '...',
                    ),
                }}
              />

              {!expanded && exceedsCharLimit && (
                <button
                  onClick={() => setExpanded(true)}
                  className="text-sm text-[#3B82F6] hover:underline block"
                >
                  {t('seeMore')}
                </button>
              )}
            </div>

            {/* Variant selection */}
            {localProduct?.variants?.length > 0 && (
              <div className="mb-4 bg-[#f5f5f5] rounded-lg p-3">
                <h3 className="font-semibold text-base mt-3 mb-2">
                  {i18n.language.startsWith('fr') ? localProduct.variants[0].frTitle : localProduct.variants[0].enTitle}
                </h3>
                <div className="flex flex-col gap-2">
                  {localProduct.variants.map((variant, index) => (
                    <VariantItem
                      key={variant._id || index}
                      variant={variant}
                      index={index}
                      selectedVariantIndex={selectedVariantIndex}
                      setSelectedVariantIndex={setSelectedVariantIndex}
                      i18n={i18n}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quantity & Add to Cart bar — hidden when price is locked behind
              login or when the merchant doesn't deliver to the user. In
              those cases we render a CTA tailored to the situation so the
              modal always has an actionable path forward. */}
          {isOutOfZone ? (
            <div className="py-3 px-2 border-t bg-white border-gray-800 w-full z-40 mt-auto sticky bottom-0">
              <p className="text-xs text-[#6F6F6F] mb-2 leading-snug">
                {t('outOfZoneNotice')}
              </p>
              <a
                href={buildOutOfZoneMailto()}
                className="block w-full h-11 leading-[44px] text-center rounded text-sm font-medium bg-[#0B1223] text-white hover:bg-opacity-90 transition-all duration-300"
              >
                {t('contactNeobox')}
              </a>
            </div>
          ) : getCurrentPrice() !== null ? (
            <div className="py-3 px-2 border-t bg-white border-gray-800 flex gap-2 w-full z-40 mt-auto sticky bottom-0">
              <div className="border border-[#E6E6E6] rounded flex items-center justify-center h-11 w-[100px] sm:w-[125px]">
                <button
                  className="px-2 font-semibold text-sm min-w-10"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={localQuantity <= 1}
                >
                  -
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={localQuantity}
                  onChange={handleQuantityInput}
                  onBlur={handleQuantityBlur}
                  className="w-full text-center font-semibold text-sm outline-none bg-transparent"
                />
                <button
                  className="px-2 font-semibold text-sm min-w-10"
                  onClick={() => handleQuantityChange(1)}
                >
                  +
                </button>
              </div>

              <button
                className={`px-2 h-11 flex-1 rounded text-sm font-medium flex items-center justify-between transition-all duration-500
                  ${localProduct.availability === 'in-stock' ? 'bg-[#0B1223] text-white hover:bg-opacity-90' : 'bg-gray-400 text-gray-700 cursor-not-allowed'}`}
                onClick={handleAddToCartClick}
                disabled={localProduct.availability !== 'in-stock'}
              >
                <p className="hidden md:block"></p>
                <p>{localProduct.availability === 'in-stock' ? t('Add to cart') : t('Out of stock')}</p>
                <p>{localProduct.availability === 'in-stock' && (
                  <span className="block">{(getCurrentPrice() * localQuantity).toFixed(2)}$</span>
                )}</p>
              </button>
            </div>
          ) : (
            <div className="py-3 px-2 border-t bg-white border-gray-800 w-full z-40 mt-auto sticky bottom-0">
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent(OPEN_LOGIN_EVENT))
                }
                className="w-full h-11 rounded text-sm font-medium bg-[#0B1223] text-white hover:bg-opacity-90 transition-all duration-500"
              >
                {t('signInToAddToCart')}
              </button>
            </div>
          )}
        </div>
      </div>
      <Toast message={toastMessage} type={toastType} />
    </div>
  );
};

export default SingleProductDetails;
